import { jsPDF } from "jspdf";
import { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress, Grid, Icon } from "@mui/material";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import boxShadows from "assets/theme/base/boxShadows";
import { restLink } from "services/config";
import { formatMoneyPunto } from "utils/formatters";

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDateTime = (value) => {
  const date = parseDateValue(value);
  if (!date) return "-";
  return date.toLocaleString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatCurrency = (value) => `$${formatMoneyPunto(Number(value || 0))}`;

const getRowCondition = (sectionKey, row) => {
  if (sectionKey === "irregular") {
    const irregularidad = row?.irregularidad || "Sin descripcion";
    const uso = row?.usoNumeroEnCliente ? ` - uso ${row.usoNumeroEnCliente}` : "";
    const total = row?.totalUsosVoucherCliente ? ` de ${row.totalUsosVoucherCliente}` : "";
    return `${irregularidad}${uso}${total}`;
  }
  if (sectionKey === "pendiente") return "Voucher pendiente sin uso";
  if (sectionKey === "vencido") return "Voucher vencido sin uso";
  return "Uso correcto";
};

const getCellValue = (columnKey, section, row) => {
  switch (columnKey) {
    case "cliente":
      return `${row?.idCliente || "-"} - ${row?.apellido || "-"}, ${row?.nombre || "-"} (DNI ${row?.dni || "-"})`;
    case "pedidoVenta":
      if (row?.idPedido || row?.idVenta) return `P${row?.idPedido || "-"}`;
      return "-";
    case "fecha":
      return formatDateTime(row?.[section?.dateField]);
    case "monto":
      return formatCurrency(row?.montoPedido);
    case "sede":
      return `${row?.sedeReporte || "Sin sede"}`;
    case "appTipo":
      return `${row?.appUsuarioAlMomento || "-"} / ${row?.tipoUsuarioAlMomento || "-"}`;
    case "condicion":
      return getRowCondition(section?.key, row);
    default:
      return "-";
  }
};

const tableStyles = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
  fontSize: "10px",
};

const thStyles = {
  border: "1px solid #d2d6da",
  backgroundColor: "#1F3A5F",
  color: "#fff",
  padding: "6px 4px",
  textAlign: "left",
};

const tdStyles = {
  border: "1px solid #d2d6da",
  padding: "4px",
  verticalAlign: "top",
  wordBreak: "break-word",
};

const summaryCardStyle = {
  border: "1px solid #e9ecef",
  borderRadius: "10px",
  p: 1.5,
  backgroundColor: "#f8f9fa",
};

const VoucherAlumnosPdf = () => {
  const { lg } = boxShadows;
  const documentRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reportData, setReportData] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await restLink.get("reportes/vouchers-alumnos-pdf-uso-correcto");
      setReportData(response?.data || null);
    } catch (error) {
      setErrorMessage("No se pudo cargar el reporte de vouchers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleGeneratePdf = async () => {
    if (!documentRef.current || !reportData) return;

    try {
      setLoadingPdf(true);
      const doc = new jsPDF({
        format: "a4",
        unit: "px",
        orientation: "landscape",
        hotfixes: ["px_scaling"],
        compress: true,
      });

      await doc.html(documentRef.current, {
        x: 10,
        y: 10,
        width: 1120,
        windowWidth: 1180,
        margin: [25, 20, 25, 20],
        autoPaging: "text",
        html2canvas: {
          scale: 0.65,
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          width: 1180,
          height: documentRef.current.scrollHeight,
        },
        callback(pdfInstance) {
          const stamp = new Date().toISOString().slice(0, 10);
          pdfInstance.save(`reporte-vouchers-alumnos-${stamp}.pdf`);
        },
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  if (loading) {
    return (
      <EmpleadoLayout>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando reporte de vouchers...</SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  if (errorMessage) {
    return (
      <EmpleadoLayout>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          flexDirection="column"
          gap={2}
        >
          <Icon fontSize="large">error_outline</Icon>
          <SoftTypography variant="h5">{errorMessage}</SoftTypography>
          <SoftButton color="info" onClick={fetchReport}>
            Reintentar
          </SoftButton>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  if (!reportData) {
    return (
      <EmpleadoLayout>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          flexDirection="column"
          gap={2}
        >
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No hay datos para mostrar.</SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  const sections = [
    reportData?.sections?.correct,
    reportData?.sections?.pendiente,
    reportData?.sections?.irregular,
  ].filter(Boolean);

  return (
    <EmpleadoLayout>
      <SoftBox p={2} display="flex" flexDirection="column" gap={2} minHeight="100vh">
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold">Reporte Integral de Vouchers</SoftTypography>
            <SoftTypography variant="caption" color="text">
              Generado (America/Argentina/Buenos_Aires): {reportData?.generatedAtBuenosAires || "-"}
            </SoftTypography>
          </SoftBox>

          <SoftBox display="flex" gap={1}>
            <SoftButton color="info" variant="outlined" onClick={fetchReport} disabled={loadingPdf}>
              <Icon>refresh</Icon>&nbsp;Actualizar
            </SoftButton>
            <SoftButton color="info" onClick={handleGeneratePdf} disabled={loadingPdf}>
              <Icon>picture_as_pdf</Icon>&nbsp;Descargar PDF
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <SoftBox sx={{ width: "100%", pb: 2 }}>
          <div ref={documentRef} style={{ width: "100%" }}>
            <SoftBox
              sx={{
                width: "100%",
                minHeight: "100%",
                backgroundColor: "#fff",
                boxShadow: lg,
                borderRadius: "12px",
                p: 3,
              }}
            >
              <SoftTypography variant="h4" fontWeight="bold" mb={1}>
                Reporte Integral de Vouchers
              </SoftTypography>
              <SoftTypography variant="body2" mb={2}>
                Ventas voucher auditadas: {reportData?.summary?.totalVentasVoucher || 0}
              </SoftTypography>

              <Grid container spacing={1.5} mb={3}>
                <Grid item xs={6} md={4} lg={2}>
                  <SoftBox sx={summaryCardStyle}>
                    <SoftTypography variant="caption" color="text">Uso correcto</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">{reportData?.summary?.totalCorrect || 0}</SoftTypography>
                  </SoftBox>
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                  <SoftBox sx={summaryCardStyle}>
                    <SoftTypography variant="caption" color="text">Pendientes</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">{reportData?.summary?.totalPendiente || 0}</SoftTypography>
                  </SoftBox>
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                  <SoftBox sx={summaryCardStyle}>
                    <SoftTypography variant="caption" color="text">Irregularidades</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">{reportData?.summary?.totalIrregular || 0}</SoftTypography>
                  </SoftBox>
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                  <SoftBox sx={summaryCardStyle}>
                    <SoftTypography variant="caption" color="text">Monto uso correcto</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      {formatCurrency(reportData?.summary?.montoTotalCorrecto)}
                    </SoftTypography>
                  </SoftBox>
                </Grid>
              </Grid>

              {sections.map((section) => (
                <SoftBox key={section.key} mb={4}>
                  <SoftTypography variant="h5" fontWeight="bold" mb={0.5}>
                    {section.title}
                  </SoftTypography>

                  {section.criteria ? (
                    <SoftTypography variant="caption" color="text" display="block" mb={1}>
                      {section.criteria}
                    </SoftTypography>
                  ) : null}

                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    Registros: {section.totalRegistros} - Total: {formatCurrency(section.totalMonto)}
                  </SoftTypography>

                  {section.groups.map((group) => (
                    <SoftBox key={`${section.key}-${group.sedeKey}`} mb={2.5}>
                      <SoftTypography variant="h6" fontWeight="bold" mb={0.5}>
                        {group.sedeTitulo}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block" mb={1}>
                        Registros: {group.totalRegistros} - Total sede: {formatCurrency(group.totalMonto)}
                      </SoftTypography>

                      {group.fechas.map((fecha) => (
                        <SoftBox key={`${section.key}-${group.sedeKey}-${fecha.fecha}`} mb={1.5}>
                          <SoftTypography variant="button" fontWeight="bold" display="block" mb={0.75}>
                            {section.dateLabel}: {fecha.fecha} - Registros: {fecha.totalRegistros} - Subtotal: {formatCurrency(fecha.subtotalMonto)}
                          </SoftTypography>

                          <table style={tableStyles}>
                            <thead>
                              <tr>
                                {section.columns.map((column) => (
                                  <th key={`${section.key}-${column.key}`} style={thStyles}>
                                    {column.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {fecha.rows.map((row, idx) => (
                                <tr key={`${section.key}-${group.sedeKey}-${fecha.fecha}-${row.idVenta || row.idCliente}-${idx}`}>
                                  {section.columns.map((column) => (
                                    <td key={`${section.key}-${column.key}-${idx}`} style={tdStyles}>
                                      {getCellValue(column.key, section, row)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  ))}
                </SoftBox>
              ))}
            </SoftBox>
          </div>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default VoucherAlumnosPdf;
